// GlobalContext.tsx
"use client";
import { createContext, useState, ReactNode, useContext, useMemo, useEffect, useCallback } from "react";
import {  AuditTrailEntry, FeatureAccessPermissions,SelectedNode,Comment,PanelType, RoleBasedFeatureAccessData, ListsFallbackProps, VendorData, IdeaVendorTabRedirectSnapshot } from "./data.types";
import { useAuth } from "./AuthContext";
import { isGuestTokenSessionUserDetails } from "@/hooks/useIsGuestTokenSession";
import { AvailableRecipeTransition } from "@/components/IngredientsList/types/recipeAndRawMaterailApiTypes";
import { getPriorityRoleState } from "@/utils/roleBasedFieldPermissions";
import type { ListAccordionExpandScope } from "@/constants/listAccordionExpandScope";


// Define the shape of the context data
interface GlobalContextType {
  menuSelected: number | null;
  setMenuSelected: (menuSelected: number | null) => void;
  menuExpanded: number | null;
  setMenuExpanded: (menuExpanded: number | null) => void;
  submenuSelected: number | null;
  setSubmenuSelected: (submenuSelected: number | null) => void;
  showPopper: HTMLElement | null;
  setShowPopper: (showPopper: HTMLElement | null) => void;
  showCreateProduct: boolean;
  setShowCreateProduct: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarVisible: boolean;
  setIsSidebarVisible: (isSidebarVisible: boolean) => void;
  isLoading: boolean;
  setIsLoading: (flag: boolean) => void;
  startLoading: () => void;
  stopLoading: () => void;
  withGlobalLoading: <T = unknown>(operation: () => Promise<T>) => Promise<T>;
  showLastBreadcrumb: { title: string; path: string; remove?: boolean };
  setShowLastBreadcrumb: (showLastBreadcrumb: any) => void;
  hasVendorInformationFormsSubmitted: boolean;
  setHasVendorInformationFormsSubmitted: (
    hasVendorInformationFormsSubmitted: boolean
  ) => void;
  showCommentsNLogs: boolean;
  setShowCommentsNLogs: (showCommentsNLogs: boolean) => void;
  vendorList: { id: string; name: string }[];
  setVendorList: React.Dispatch<
    React.SetStateAction<{ id: string; name: string }[]>
  >;
  commentCount: number | null;
  setCommentCount: React.Dispatch<React.SetStateAction<number | null>>;
  correctiveActionNotesCount: number | null;
  setCorrectiveActionNotesCount: React.Dispatch<React.SetStateAction<number | null>>;
  shoppingListComments: Comment[];
  setShoppingListComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  showProductInfo: boolean;
  setShowProductInfo: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIdeaName: string;
  setSelectedIdeaName: React.Dispatch<React.SetStateAction<string>>;
  isCommentVisible: boolean;
  setIsCommentVisible: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: number;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
  activeInfoFormIndex: number;
  setActiveInfoFormIndex: React.Dispatch<React.SetStateAction<number>>;
  activeVendorTab: number;
  setActiveVendorTab: React.Dispatch<React.SetStateAction<number>>;
  /** Index into `vendorDetails[group].vendorList` for the active vendor group (Active Projects). */
  activeVendorVersionIndex: number;
  setActiveVendorVersionIndex: React.Dispatch<React.SetStateAction<number>>;
  taskCount: number;
  setTaskCount: React.Dispatch<React.SetStateAction<number>>;
  shouldFetchShoppingList: boolean;
  setShouldFetchShoppingList: React.Dispatch<React.SetStateAction<boolean>>;
  isShoppingListLoading: boolean;
  setIsShoppingListLoading: React.Dispatch<React.SetStateAction<boolean>>;
  commentsComponentActive: string;
  setCommentsComponentActive: React.Dispatch<React.SetStateAction<string>>;
  vendorAssessmentData: { [vendorId: string]: any };
  setVendorAssessmentData: React.Dispatch<React.SetStateAction<{ [vendorId: string]: any }>>;

  // Create Idea and Vendor Forms
  vendorUpdated: boolean;
  setVendorUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  isProductInfoSaveInProgress: boolean;
  setIsProductInfoSaveInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  addEditModalType: string;
  setAddEditModalType: React.Dispatch<React.SetStateAction<string>>;

  userLogs: AuditTrailEntry[];
  setUserLogs: React.Dispatch<React.SetStateAction<AuditTrailEntry[]>>;

  roleBasedAccessPermissions: FeatureAccessPermissions;
  setRoleBasedAccessPermissions: React.Dispatch<
    React.SetStateAction<FeatureAccessPermissions>
  >;

  selectedNodePath: { id: string; name: string }[];
  setSelectedNodePath: (path: { id: string; name: string }[]) => void;

  selectedAuditLogPage: number;
  setSelectedAuditLogPage: React.Dispatch<React.SetStateAction<number>>;

  isAuditLogPageHasMoreData: boolean;
  setIsAuditLogPageHasMoreData: React.Dispatch<React.SetStateAction<boolean>>;

  selectedNode: SelectedNode;
  setSelectedNode: React.Dispatch<React.SetStateAction<SelectedNode>>;
  glossaryComments: Comment[];
  setGlossaryComments: React.Dispatch<React.SetStateAction<Comment[]>>;

  activePanel: PanelType;
  setActivePanel: React.Dispatch<React.SetStateAction<PanelType>>;
  isCANTLModalOpen: boolean;
  setIsCANTLModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCANTLModalAutoClosed: boolean;
  setIsCANTLModalAutoClosed: React.Dispatch<React.SetStateAction<boolean>>;

  // Prepare for Panel (PFP)
  activePFPTab: number;
  setActivePFPTab: (tabIndex: number) => void;
  nextPFPVendorId: number;
  setNextPFPVendorId: (tabIndex: number) => void;
  activePFPVendorId: string;
  setActivePFPVendorId: (vendorId: string) => void;
  listsFallbackScreenData: ListsFallbackProps | null;
  setListsFallbackScreenData: React.Dispatch<React.SetStateAction<ListsFallbackProps | null>>;
  
  // Selected active project data (with page refresh support)
  selectedActiveProject: any | null;
  setSelectedActiveProject: (data: any | null) => void;

  // Active projects product info data
  activeProjectsProductInfo: any | null;
  setActiveProjectsProductInfo: (data: any | null) => void;

  /**
   * `creationDate` from the latest GET `/productInfo/details` response.
   */
  productInfoCreationDate: string | null;
  productInfoCreationDateIdeaId: string | number | null;
  setProductInfoCreationDateFromDetails: (
    response: { creationDate?: unknown; ideaId?: unknown; id?: unknown } | null | undefined,
    ideaIdFallback?: string | number
  ) => void;
  clearProductInfoCreationDate: () => void;

  // Selected vendor data
  selectedActiveProjectVendors: VendorData[]
  setSelectedActiveProjectVendors: React.Dispatch<React.SetStateAction<VendorData[]>>;
  selectedActiveProjectSelectedVendor: any | null;
  setSelectedActiveProjectSelectedVendor: (data: any | null) => void;
  
  // Global isVendor state based on user type
  isVendor: boolean;

  // Global userType state based on user type
  userType: string;

  // Priority role state (Business Admin > Category Manager > null)
  priorityRole: string | null;

  // Available recipe transitions
  availableRecipeTransition: AvailableRecipeTransition | null;
  setAvailableRecipeTransition: (data: AvailableRecipeTransition | null) => void;
  
  // Food Safety active site ID (for site-specific comments)
  foodSafetyActiveSiteId: string | null;
  setFoodSafetyActiveSiteId: (siteId: string | null) => void;

  /**
   * Food Safety nested tab (0 = Vendor Q&A, 1 = Audit Information, 2 = Other Attachments).
   * Used on Active Projects / Launched Products so the sub-tab survives vendor version changes and remounts.
   */
  foodSafetyNestedTabIndex: number;
  setFoodSafetyNestedTabIndex: (index: number) => void;

  /**
   * Last selected Food Safety facility site id (Active Projects / Launched Products).
   * Used to re-select the same site after vendor version / assessment change when that site still exists in the bundle.
   */
  foodSafetyPreferredSiteId: string | null;
  setFoodSafetyPreferredSiteId: (siteId: string | null) => void;
  
  // Active Projects current tab key (for tab-specific comments)
  activeProjectsTabKey: string | null;
  setActiveProjectsTabKey: (tabKey: string | null) => void;
  
  // Active Projects current variant index (for variant-specific comments)
  activeProjectsVariantIndex: number;
  setActiveProjectsVariantIndex: (variantIndex: number) => void;
  
  activeProjectVariantId: string | null | number | undefined;
  setActiveProjectVariantId: (variantId: string | null | number | undefined) => void;
  
  // Active Projects Recipe/Raw Materials sub-tab index (0 = Recipe, 1 = Raw Materials)
  activeProjectsSubTabIndex: number;
  setActiveProjectsSubTabIndex: (subTabIndex: number) => void;
  
  // Vendor contact redirect flag (set to true when redirecting from vendor onboarding confirm)
  isRedirectingFromVendorDataReviewConfirm: boolean;
  setIsRedirectingFromVendorDataReviewConfirm: React.Dispatch<React.SetStateAction<boolean>>;

  /** Set when Active Projects vendor tab triggers redirect to another stage; cleared via setter. */
  ideaVendorTabRedirectSnapshot: IdeaVendorTabRedirectSnapshot | null;
  setIdeaVendorTabRedirectSnapshot: React.Dispatch<
    React.SetStateAction<IdeaVendorTabRedirectSnapshot | null>
  >;

  /** Expanded multi-version parent product ids per list (survives list unmount / browser back). */
  listAccordionExpandedProductIdsByScope: Partial<Record<ListAccordionExpandScope, string[]>>;
  setListAccordionExpandedProductIdsByScope: React.Dispatch<
    React.SetStateAction<Partial<Record<ListAccordionExpandScope, string[]>>>
  >;
}

// Create the context
const GlobalStateContext = createContext<GlobalContextType | undefined>(
  undefined
);

// Provider component
export const GlobalStateProvider = ({ children }: { children: ReactNode }) => {
  const { userDetails } = useAuth();
  const [menuSelected, setMenuSelected] = useState<number | null>(null);
  const [menuExpanded, setMenuExpanded] = useState<number | null>(null);
  const [submenuSelected, setSubmenuSelected] = useState<number | null>(null);
  const [showPopper, setShowPopper] = useState<HTMLElement | null>(null);
  const [showCreateProduct, setShowCreateProduct] = useState<boolean>(false);
  const [showLastBreadcrumb, setShowLastBreadcrumb] = useState<{
    title: string;
    path: string;
  }>({ title: "", path: "" });
  
  const [pendingRequests, setPendingRequests] = useState<number>(0);
  const isLoading = pendingRequests > 0;
  const startLoading = useCallback(() => setPendingRequests(count => count + 1), []);
  const stopLoading = useCallback(() => setPendingRequests(count => Math.max(0, count - 1)), []);
 
  const setIsLoading = useCallback((flag: boolean) => (flag ? startLoading() : stopLoading()), [startLoading, stopLoading]);
  const withGlobalLoading = useCallback(async <T,>(operation: () => Promise<T>): Promise<T> => {
    startLoading();
    try {
      return await operation();
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(false);
  const [
    hasVendorInformationFormsSubmitted,
    setHasVendorInformationFormsSubmitted,
  ] = useState(false);
  const [showCommentsNLogs, setShowCommentsNLogs] = useState<boolean>(false);
  const [isCommentVisible, setIsCommentVisible] = useState<boolean>(false);
  
  // For storing all the vendor list
  const [vendorList, setVendorList] = useState<{ id: string; name: string }[]>([]);
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const [correctiveActionNotesCount, setCorrectiveActionNotesCount] = useState<number | null>(null);
  const [shoppingListComments, setShoppingListComments] = useState<Comment[]>([]);
  const [showProductInfo, setShowProductInfo] = useState(false);
  const [selectedIdeaName, setSelectedIdeaName] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0); // for create product and vendor information forms
  const [activeInfoFormIndex, setActiveInfoFormIndex] = useState<number>(0);
  const [activeVendorTab, setActiveVendorTab] = useState<number>(0);
  const [activeVendorVersionIndex, setActiveVendorVersionIndex] = useState<number>(0);
  const [taskCount, setTaskCount] = useState<number>(0);
  const [shouldFetchShoppingList, setShouldFetchShoppingList] = useState<boolean>(false);
  const [isShoppingListLoading, setIsShoppingListLoading] = useState<boolean>(false);
  const [vendorUpdated, setVendorUpdated] = useState<boolean>(false); // For storing the updates in vendor information form shared for product info and vendor info
  const [isProductInfoSaveInProgress, setIsProductInfoSaveInProgress] = useState<boolean>(false);
  const [addEditModalType, setAddEditModalType] = useState<string>("");
  const [userLogs, setUserLogs] = useState<AuditTrailEntry[]>([]);
  const [roleBasedAccessPermissions, setRoleBasedAccessPermissions] = useState<FeatureAccessPermissions>({});
  const [selectedNodePath, setSelectedNodePath] = useState<{ id: string; name: string }[]>([]);
  const [selectedAuditLogPage, setSelectedAuditLogPage] = useState<number>(0);
  const [isAuditLogPageHasMoreData, setIsAuditLogPageHasMoreData] = useState<boolean>(true);
  const [commentsComponentActive, setCommentsComponentActive] = useState<string>("");
  const [vendorAssessmentData, setVendorAssessmentData] = useState<{ [vendorId: string]: any }>({});
  const [selectedNode, setSelectedNode] = useState<SelectedNode>({ nodeId: "", field: "" });
  const [glossaryComments, setGlossaryComments] = useState<Comment[]>([]);
  const [activePanel, setActivePanel] = useState<"comments" | "userlog" | null>(null);
  // Drop stale audit logs whenever the audit log panel isn't showing, so a later open
  // (e.g. from another tab) never flashes the previous tab's data before the fetch runs.
  useEffect(() => {
    if (activePanel !== "userlog") setUserLogs([]);
  }, [activePanel]);
  const [isCANTLModalOpen, setIsCANTLModalOpen] = useState<boolean>(false);
  const [isCANTLModalAutoClosed, setIsCANTLModalAutoClosed] = useState<boolean>(false);
  const [activePFPTab, setActivePFPTab] = useState<number>(0);
  const [ nextPFPVendorId, setNextPFPVendorId] = useState<number>(0);
  const [activePFPVendorId, setActivePFPVendorId] = useState<string>("");
  const [listsFallbackScreenData, setListsFallbackScreenData] = useState<ListsFallbackProps | null>(null);
  // Initialize selectedActiveProjectVendors state
  const [selectedActiveProjectVendors, setSelectedActiveProjectVendors] = useState<VendorData[]>([]);
  const [selectedActiveProjectSelectedVendor, setSelectedActiveProjectSelectedVendor] = useState<any | null>(null);
  // Initialize selectedActiveProject from sessionStorage if available (handles page refresh)
  const [selectedActiveProject, setSelectedActiveProject] = useState<any | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('selectedActiveProject');
      return stored ? JSON.parse(stored) : null;
    }
    return null;
  });

  // Initialize activeProjectsProductInfo state
  const [activeProjectsProductInfo, setActiveProjectsProductInfo] = useState<any | null>(null);

  // creationDate from latest /productInfo/details
  const [productInfoCreationDate, setProductInfoCreationDate] = useState<string | null>(null);
  const [productInfoCreationDateIdeaId, setProductInfoCreationDateIdeaId] = useState<
    string | number | null
  >(null);

  const setProductInfoCreationDateFromDetails = useCallback(
    (
      response: { creationDate?: unknown; ideaId?: unknown; id?: unknown } | null | undefined,
      ideaIdFallback?: string | number
    ) => {
      const rawDate = response?.creationDate;
      const nextCreationDate =
        rawDate == null || String(rawDate).trim() === "" ? null : String(rawDate).trim();

      const rawIdeaId = response?.ideaId ?? response?.id ?? ideaIdFallback ?? null;
      const nextIdeaId =
        rawIdeaId == null || String(rawIdeaId).trim() === ""
          ? null
          : (typeof rawIdeaId === "number" || typeof rawIdeaId === "string"
              ? rawIdeaId
              : String(rawIdeaId));

      setProductInfoCreationDate(nextCreationDate);
      setProductInfoCreationDateIdeaId(nextIdeaId);
    },
    []
  );

  const clearProductInfoCreationDate = useCallback(() => {
    setProductInfoCreationDate(null);
    setProductInfoCreationDateIdeaId(null);
  }, []);

  const isVendor = useMemo(() => {
    return (
      String(userDetails?.type ?? "").toUpperCase() === "VENDOR"
    );
  }, [userDetails]);

  // const isGuestToken = useMemo(() => {
  //   return (
  //     !isGuestTokenSessionUserDetails(userDetails)
  //   );
  // }, [userDetails]);

  // Compute userType based on userDetails.type
  const userType = useMemo(() => {
    return userDetails?.type?.toUpperCase() || "";
  }, [userDetails?.type]);

  // Compute priorityRole based on userDetails.roles (Business Admin > Category Manager)
  const priorityRole = useMemo(() => {
    const roles = userDetails?.roles || [];
    return getPriorityRoleState(roles);
  }, [userDetails?.roles]);

  // Available recipe transition state
  const [availableRecipeTransition, setAvailableRecipeTransition] = useState<AvailableRecipeTransition | null>(null);
  
  // Food Safety active site ID state
  const [foodSafetyActiveSiteId, setFoodSafetyActiveSiteId] = useState<string | null>(null);

  const [foodSafetyNestedTabIndex, setFoodSafetyNestedTabIndex] = useState<number>(0);

  const [foodSafetyPreferredSiteId, setFoodSafetyPreferredSiteId] = useState<string | null>(null);
  
  // Active Projects current tab key state
  const [activeProjectsTabKey, setActiveProjectsTabKey] = useState<string | null>(null);
  
  // Active Projects current variant index state
  const [activeProjectsVariantIndex, setActiveProjectsVariantIndex] = useState<number>(0);

  // Active Projects current variant id state
  const [activeProjectVariantId, setActiveProjectVariantId] = useState<string | null | number | undefined>(null);
  
  // Active Projects Recipe/Raw Materials sub-tab index state
  const [activeProjectsSubTabIndex, setActiveProjectsSubTabIndex] = useState<number>(0);
  
  // Vendor contact redirect flag state
  const [isRedirectingFromVendorDataReviewConfirm, setIsRedirectingFromVendorDataReviewConfirm] = useState<boolean>(false);

  const [ideaVendorTabRedirectSnapshot, setIdeaVendorTabRedirectSnapshot] =
    useState<IdeaVendorTabRedirectSnapshot | null>(null);

  const [listAccordionExpandedProductIdsByScope, setListAccordionExpandedProductIdsByScope] = useState<
    Partial<Record<ListAccordionExpandScope, string[]>>
  >({});

  // Custom setter that updates both state and sessionStorage
  const updateSelectedActiveProject = (data: any | null) => {
    setSelectedActiveProject(data);
    if (typeof window !== 'undefined') {
      if (data) {
        sessionStorage.setItem('selectedActiveProject', JSON.stringify(data));
        console.log('💾 Stored active project data in sessionStorage for page refresh support');
      } else {
        sessionStorage.removeItem('selectedActiveProject');
        console.log('🧹 Cleared active project data from sessionStorage');
      }
    }
  };

  // Cleanup sessionStorage when user navigates away from active projects
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleBeforeUnload = () => {
        // Keep data in sessionStorage for page refresh, but could add logic here if needed
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, []);

  useEffect(() => {
    setFoodSafetyNestedTabIndex(0);
    setFoodSafetyPreferredSiteId(null);
  }, [selectedActiveProject?.id]);

  // useEffect(() => {
  //   setActiveVendorVersionIndex(0);
  // }, [selectedActiveProject?.id]);

  const ContextValue = useMemo(
    () => ({
      menuSelected,
      setMenuSelected,
      menuExpanded,
      setMenuExpanded,
      submenuSelected,
      setSubmenuSelected,
      showPopper,
      setShowPopper,
      showCreateProduct,
      setShowCreateProduct,
      isSidebarVisible,
      setIsSidebarVisible,
      showLastBreadcrumb,
      setShowLastBreadcrumb,
      hasVendorInformationFormsSubmitted,
      setHasVendorInformationFormsSubmitted,
      vendorUpdated, 
      setVendorUpdated,

      vendorList,
      setVendorList,
      
      showCommentsNLogs,
      setShowCommentsNLogs,
      isCommentVisible,
      setIsCommentVisible,

      isLoading,
      setIsLoading,
      startLoading,
      stopLoading,
      withGlobalLoading,
      commentCount,
      setCommentCount,
      correctiveActionNotesCount,
      setCorrectiveActionNotesCount,
      shoppingListComments,
      setShoppingListComments,
      showProductInfo,
      setShowProductInfo,
      selectedIdeaName,
      setSelectedIdeaName,
      activeTab,
      setActiveTab,
      activeInfoFormIndex,
      setActiveInfoFormIndex,
      activeVendorTab,
      setActiveVendorTab,
      activeVendorVersionIndex,
      setActiveVendorVersionIndex,
      taskCount,
      setTaskCount,
      shouldFetchShoppingList,
      setShouldFetchShoppingList,
      isShoppingListLoading,
      setIsShoppingListLoading,
      isProductInfoSaveInProgress,
      setIsProductInfoSaveInProgress,
      addEditModalType,
      setAddEditModalType,
      userLogs,
      setUserLogs,
      roleBasedAccessPermissions,
      setRoleBasedAccessPermissions,
      selectedNodePath,
      setSelectedNodePath,
      selectedAuditLogPage,
      setSelectedAuditLogPage,
      isAuditLogPageHasMoreData,
      setIsAuditLogPageHasMoreData,
      commentsComponentActive,
      setCommentsComponentActive,
      vendorAssessmentData,
      setVendorAssessmentData,
      selectedNode,
      setSelectedNode,
      glossaryComments,
      setGlossaryComments,
      activePanel,
      setActivePanel,
      isCANTLModalOpen,
      setIsCANTLModalOpen,
      isCANTLModalAutoClosed,
      setIsCANTLModalAutoClosed,
      // Prepare for Panel (PFP)
      activePFPTab,
      setActivePFPTab,
      activePFPVendorId,
      setActivePFPVendorId,
      nextPFPVendorId,
      setNextPFPVendorId,
      listsFallbackScreenData,
      setListsFallbackScreenData,
      selectedActiveProject,
      activeProjectsProductInfo,
      setActiveProjectsProductInfo,
      productInfoCreationDate,
      productInfoCreationDateIdeaId,
      setProductInfoCreationDateFromDetails,
      clearProductInfoCreationDate,
      selectedActiveProjectVendors,
      setSelectedActiveProjectVendors,
      selectedActiveProjectSelectedVendor,
      setSelectedActiveProjectSelectedVendor,
      setSelectedActiveProject: updateSelectedActiveProject,
      isVendor,
      userType,
      priorityRole,
      availableRecipeTransition,
      setAvailableRecipeTransition,
      foodSafetyActiveSiteId,
      setFoodSafetyActiveSiteId,
      foodSafetyNestedTabIndex,
      setFoodSafetyNestedTabIndex,
      foodSafetyPreferredSiteId,
      setFoodSafetyPreferredSiteId,
      activeProjectsTabKey,
      setActiveProjectsTabKey,
      activeProjectsVariantIndex,
      setActiveProjectsVariantIndex,
      activeProjectVariantId,
      setActiveProjectVariantId,
      activeProjectsSubTabIndex,
      setActiveProjectsSubTabIndex,
      isRedirectingFromVendorDataReviewConfirm,
      setIsRedirectingFromVendorDataReviewConfirm,
      ideaVendorTabRedirectSnapshot,
      setIdeaVendorTabRedirectSnapshot,
      listAccordionExpandedProductIdsByScope,
      setListAccordionExpandedProductIdsByScope,

    }),
    [
      menuSelected,
      setMenuSelected,
      menuExpanded,
      setMenuExpanded,
      submenuSelected,
      setSubmenuSelected,
      showPopper,
      setShowPopper,
      showCreateProduct,
      setShowCreateProduct,
      isSidebarVisible,
      setIsSidebarVisible,
      showLastBreadcrumb,
      setShowLastBreadcrumb,
      hasVendorInformationFormsSubmitted,
      setHasVendorInformationFormsSubmitted,
      showCommentsNLogs,
      setShowCommentsNLogs,
      isLoading,
      setIsLoading,
      startLoading,
      stopLoading,
      withGlobalLoading,
      vendorList,
      setVendorList,
      vendorUpdated,
      setVendorUpdated,
      commentCount,
      setCommentCount,
      correctiveActionNotesCount,
      setCorrectiveActionNotesCount,
      shoppingListComments,
      setShoppingListComments,
      showProductInfo,
      setShowProductInfo,
      selectedIdeaName,
      setSelectedIdeaName,
      activeTab,
      setActiveTab,
      activeInfoFormIndex,
      setActiveInfoFormIndex,
      activeVendorTab,
      setActiveVendorTab,
      activeVendorVersionIndex,
      setActiveVendorVersionIndex,
      isCommentVisible, 
      setIsCommentVisible,
      taskCount,
      setTaskCount,
      shouldFetchShoppingList,
      setShouldFetchShoppingList,
      isShoppingListLoading,
      setIsShoppingListLoading,
      isProductInfoSaveInProgress,
      setIsProductInfoSaveInProgress,
      addEditModalType,
      setAddEditModalType,
      userLogs,
      setUserLogs,
      roleBasedAccessPermissions,
      setRoleBasedAccessPermissions,
      selectedNodePath,
      setSelectedNodePath,
      commentsComponentActive,
      setCommentsComponentActive,
      selectedNode,
      setSelectedNode,
      glossaryComments,
      setGlossaryComments,
      selectedAuditLogPage,
      setSelectedAuditLogPage,
      isAuditLogPageHasMoreData,
      setIsAuditLogPageHasMoreData,
      activePanel,
      setActivePanel,
      isCANTLModalOpen,
      setIsCANTLModalOpen,
      isCANTLModalAutoClosed,
      setIsCANTLModalAutoClosed,
      // Prepare for Panel (PFP)
      activePFPTab,
      setActivePFPTab,
      activePFPVendorId,
      setActivePFPVendorId,
      nextPFPVendorId,
      setNextPFPVendorId,
      listsFallbackScreenData,
      setListsFallbackScreenData,
      selectedActiveProject,
      activeProjectsProductInfo,
      setActiveProjectsProductInfo,
      productInfoCreationDate,
      productInfoCreationDateIdeaId,
      setProductInfoCreationDateFromDetails,
      clearProductInfoCreationDate,
      selectedActiveProjectVendors,
      setSelectedActiveProjectVendors,
      updateSelectedActiveProject,
      isVendor,
      userType,
      priorityRole,
      availableRecipeTransition,
      setAvailableRecipeTransition,
      foodSafetyActiveSiteId,
      setFoodSafetyActiveSiteId,
      foodSafetyNestedTabIndex,
      setFoodSafetyNestedTabIndex,
      foodSafetyPreferredSiteId,
      setFoodSafetyPreferredSiteId,
      activeProjectsTabKey,
      setActiveProjectsTabKey,
      activeProjectsVariantIndex,
      setActiveProjectsVariantIndex,
      activeProjectVariantId,
      setActiveProjectVariantId,
      activeProjectsSubTabIndex,
      setActiveProjectsSubTabIndex,
      isRedirectingFromVendorDataReviewConfirm,
      setIsRedirectingFromVendorDataReviewConfirm,
      ideaVendorTabRedirectSnapshot,
      setIdeaVendorTabRedirectSnapshot,
      listAccordionExpandedProductIdsByScope,
      setListAccordionExpandedProductIdsByScope,
    ]
  );




  return (
    <GlobalStateContext.Provider value={ContextValue}>
      {children}
    </GlobalStateContext.Provider>
  );
};

// Custom hook to use the GlobalContext
export const useGlobalStateContext = () => {
  const context = useContext(GlobalStateContext);
  if (!context) {
    throw new Error(
      "useGlobalStateContext must be used within a GlobalStateProvider"
    );
  }
  return context;
};
